import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, takeUntil } from 'rxjs';

import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { TaxRate } from '../../../models/tax-rate.model';
import { TaxRateService } from '../../../services/tax-rate.service';
import { MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToastModule } from 'primeng/toast';
import { DecimalPipe } from '@angular/common';
import { PaginationService } from '../../../../shared/services/pagination.service';
import { AddressService, StateOption } from '../../../../shared/services/address.service';

@Component({
  selector: 'app-tax-rate-list',
  imports: [
    FormsModule,
    InputTextModule,
    TableModule,
    ButtonModule,
    ToolbarModule,
    BreadcrumbModule,
    ToastModule,
    RouterLink,
    DecimalPipe,
  ],
  templateUrl: './tax-rate-list.component.html',
})
export class TaxRateListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private taxRateService = inject(TaxRateService);
  private messageService = inject(MessageService);
  private addressService = inject(AddressService);
  paginationService = inject(PaginationService);

  states = this.addressService.getStates();
  taxRates = signal<TaxRate[]>([]);

  pageNumber = signal<number>(1);
  pageSize = signal<number>(10);
  totalRecords = signal<number>(0);
  totalPages = signal<number>(0);

  loading = signal(false);

  countryFilter?: string;
  stateFilter?: string;
  cityFilter?: string;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  getStateLabel(state: string): string {
    return this.states.find((option: StateOption) => option.value === state)
      ?.label ?? state;
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize(),
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.taxRateService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.countryFilter,
        this.stateFilter,
        this.cityFilter,
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.taxRates.set(page.items as TaxRate[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar as taxas',
              life: 3000,
            });
          }
        },
      });
  }
}
